/**
 * LeafletMap — open-source tiled world map (Leaflet + OpenStreetMap) for plotting
 * active-user traffic (FEAT-ANALYTICS-001). No API key, no Google Maps. Pan/zoom
 * like a normal slippy map; traffic drawn as proportional circle markers at city
 * coordinates (precise) and country centroids (coarse fallback), sized by users.
 *
 * Note: tiles load from tile.openstreetmap.org — fine in dev; on a portal with a
 * strict CSP, allow img-src/connect-src for the tile host (or swap tile provider).
 */
import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { countryName, cityLatLng, countryLatLng } from './geo';
import { brandColor } from './components';
import { focusCluster } from './mapFocus';

// Inject the live "pulse" keyframes once (for realtime markers).
(function injectPulseCSS() {
  if (typeof document === 'undefined' || document.getElementById('xn-rt-pulse-css')) return;
  const s = document.createElement('style');
  s.id = 'xn-rt-pulse-css';
  s.textContent =
    // GA-style: a translucent blue disc with a solid edge + an animated pulse ring.
    '.xn-rt-dot{display:block;border-radius:50%;background:rgba(37,99,235,.5);border:1.5px solid #2563eb;box-shadow:0 0 0 rgba(37,99,235,.4);animation:xnrtpulse 2s ease-out infinite;box-sizing:border-box;}' +
    '@keyframes xnrtpulse{0%{box-shadow:0 0 0 0 rgba(37,99,235,.4)}70%{box-shadow:0 0 0 12px rgba(37,99,235,0)}100%{box-shadow:0 0 0 0 rgba(37,99,235,0)}}';
  document.head.appendChild(s);
})();

export function LeafletMap({ byCountry = [], byCity = [], height = 460, pulse = false, fitData = false, fitMaxZoom = 6, fitMinZoom = 4 }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const dataLayerRef = useRef(null);
  const fittedRef = useRef(false);

  // Create the map once.
  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, {
      minZoom: 1, maxZoom: 14, worldCopyJump: true, scrollWheelZoom: true, zoomControl: false,
    }).setView([30, 5], 2);
    L.control.zoom({ position: 'topright' }).addTo(map); // keep top-left clear for the live counter overlay
    // Esri World Light Gray — a free (no API key) light-grey basemap, GA-style.
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}', {
      attribution: 'Tiles &copy; Esri',
      maxZoom: 16,
    }).addTo(map);
    // Transparent label overlay (country/city names) on top of the grey canvas.
    L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}', {
      maxZoom: 16, opacity: 0.9,
    }).addTo(map);
    mapRef.current = map;
    // Leaflet needs a size recalculation once its container has laid out.
    setTimeout(() => map.invalidateSize(), 150);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // Redraw traffic markers whenever the data changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (dataLayerRef.current) { map.removeLayer(dataLayerRef.current); dataLayerRef.current = null; }

    const brand = brandColor();
    const points = [];
    // Precise city dots.
    (byCity || []).forEach((r) => {
      const g = cityLatLng(r.label);
      if (g) points.push({ lat: g.lat, lng: g.lon, v: Number(r.value || 0), label: `${r.label}${r.country ? ', ' + countryName(r.country) : ''}`, kind: 'city' });
    });
    // Country-centroid circles — only for countries with no resolved city dot,
    // so we don't double-plot the same traffic.
    const cityCountrySet = new Set((byCity || []).map((r) => (r.country ? String(r.country).toUpperCase() : null)).filter(Boolean));
    (byCountry || []).forEach((r) => {
      const code = String(r.label || '').toUpperCase();
      if (cityCountrySet.has(code)) return;
      const g = countryLatLng(code);
      if (g) points.push({ lat: g[0], lng: g[1], v: Number(r.value || 0), label: countryName(code), kind: 'country' });
    });

    const maxV = Math.max(1, ...points.map((p) => p.v));
    const group = L.layerGroup();
    points.forEach((p) => {
      // Bubble DIAMETER scales with traffic (√ for area-proportionality): ~7px for
      // the smallest location up to ~26px for the busiest.
      const size = 7 + 19 * Math.sqrt(p.v / maxV);
      const tip = `<b>${p.label}</b><br/>${p.v.toLocaleString()} active users`;
      let marker;
      if (pulse) {
        const d = Math.round(size);
        marker = L.marker([p.lat, p.lng], {
          icon: L.divIcon({
            className: '',
            html: `<span class="xn-rt-dot" style="width:${d}px;height:${d}px;margin-left:${-d / 2}px;margin-top:${-d / 2}px;display:block;"></span>`,
            iconSize: [0, 0],
          }),
        });
      } else {
        // Translucent disc with a solid edge (GA style) — overlaps read darker.
        marker = L.circleMarker([p.lat, p.lng], {
          radius: size / 2, color: brand, weight: 1.5, opacity: 0.9, fillColor: brand, fillOpacity: 0.5,
        });
      }
      marker.bindTooltip(tip, { direction: 'top', opacity: 1 }).addTo(group);
    });
    group.addTo(map);
    dataLayerRef.current = group;

    // Auto-position/zoom to WHERE THE TRAFFIC IS — once, so later refreshes and
    // the user's own pan/zoom aren't fought.
    if (fitData && points.length && !fittedRef.current) {
      // Focus cluster = the heaviest locations that together carry the bulk
      // (~70%) of the traffic. A handful of far-flung outliers no longer force a
      // zoomed-out world view where country names are unreadable.
      const { focus, busiest } = focusCluster(points, { coverage: 0.7 });
      const apply = () => {
        map.invalidateSize();
        if (focus.length === 1) {
          // A single dominant location — center on it at a legible zoom.
          map.setView([busiest.lat, busiest.lng], Math.max(fitMinZoom, 5));
          return;
        }
        // Frame ALL located traffic (not just the ~70% bulk), capped by fitMaxZoom so a
        // concentrated tenant still gets a legible zoom. Do NOT snap onto the single
        // busiest region when the audience is globally spread — that hid genuinely global
        // audiences (e.g. a worldwide charity whose #1 country is AU but with many users
        // across Europe/Middle East). A world view that shows everyone beats a tight view
        // that hides most of them.
        map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [30, 30], maxZoom: fitMaxZoom, animate: false });
      };
      // Defer so the container has its real size (esp. just after mount).
      map.whenReady(() => setTimeout(apply, 60));
      fittedRef.current = true;
    }
  }, [JSON.stringify(byCountry), JSON.stringify(byCity), fitData, fitMaxZoom, fitMinZoom]);

  return <div ref={elRef} style={{ height, width: '100%', borderRadius: 10, overflow: 'hidden', zIndex: 0 }} />;
}

export default LeafletMap;
